// What the portal reads from the API, cached by TanStack Query under one key each, so every page shares it.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { unitsOf, type components, type Units } from '@tune/shared';
import { data, type Api } from './api';
import { useAuth } from './auth';

export const keys = {
  account: ['account'] as const,
  vehicles: ['vehicles'] as const,
  devices: ['devices'] as const,
  vehicle: (id: string) => ['vehicle', id] as const,
  settings: (id: string) => ['settings', id] as const,
  history: (id: string) => ['settings-history', id] as const,
  connections: (id: string) => ['connections', id] as const,
  service: (id: string) => ['service', id] as const,
  library: ['library'] as const,
  item: (id: string) => ['library', id] as const,
  shared: ['shared'] as const,
  catalogue: ['catalogue'] as const,
  vehicleLibrary: (id: string) => ['vehicle-library', id] as const,
  records: (id: string) => ['records', id] as const,
  bulletin: ['bulletin'] as const,
  datalogs: (id: string) => ['datalogs', id] as const,
  /** A car's latest datalogs, for Telemetry and the Analyser: under `datalogs`, so invalidating that covers both. */
  latestDatalogs: (id: string) => ['datalogs', id, 'latest'] as const,
};

/** The signed-in owner's account: their name, role and what it holds. */
export function useAccount(enabled = true) {
  const api = useAuth().api('owners');
  return useQuery({ queryKey: keys.account, queryFn: () => data(api.GET('/account')), enabled });
}

export function useVehicles() {
  const api = useAuth().api('owners');
  return useQuery({ queryKey: keys.vehicles, queryFn: () => data(api.GET('/vehicles')) });
}

export function useDevices(enabled = true) {
  const api = useAuth().api('owners');
  return useQuery({ queryKey: keys.devices, queryFn: () => data(api.GET('/devices')), enabled });
}

/** A car, shown at once from the garage's list while it's read on its own. */
export function useVehicle(id: string) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: keys.vehicle(id),
    queryFn: () => data(api.GET('/vehicles/{vehicleId}', { params: { path: { vehicleId: id } } })),
    placeholderData: () =>
      queryClient.getQueryData<components['schemas']['Vehicles']>(keys.vehicles)?.vehicles.find((car) => car.id === id),
  });
}

/** After a car or a device changed: the cars, the devices and each car are read again. */
export function useCarsChanged(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.vehicles }),
      queryClient.invalidateQueries({ queryKey: keys.devices }),
      queryClient.invalidateQueries({ queryKey: ['vehicle'] }),
    ]);
  };
}

/** A car's settings, with the revision they're at (their ETag), for changing them from that revision. */
export function useSettings(id: string) {
  const api = useAuth().api('owners');
  return useQuery(settingsQuery(api, id));
}

/** The units a car's settings choose: what the portal shows its distances and speeds in. */
export function useUnits(id: string): Units | null {
  const settings = useSettings(id);
  return settings.data ? unitsOf(settings.data.values) : null;
}

/** How many datalogs of each car Telemetry and the Analyser list: the latest. */
export const LATEST_DATALOGS = 50;

/** A car's latest datalogs, newest first. */
export function latestDatalogs(api: Api, vehicleId: string) {
  return {
    queryKey: keys.latestDatalogs(vehicleId),
    queryFn: () =>
      data(
        api.GET('/vehicles/{vehicleId}/datalogs', {
          params: { path: { vehicleId }, query: { limit: LATEST_DATALOGS } },
        }),
      ),
  };
}

/** A car's settings, read once and shared with its Settings page. */
export function settingsQuery(api: Api, vehicleId: string) {
  return {
    queryKey: keys.settings(vehicleId),
    queryFn: () => data(api.GET('/vehicles/{vehicleId}/settings', { params: { path: { vehicleId } } })),
  };
}
